import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getHelpDeskAccess,
  getHelpDeskProjects,
  getMyTickets,
  getManagedTickets,
  getSingleTicket,
  createTicket,
  updateTicket,
  addTicketReply,
  uploadTicketAttachments,
  deleteTicket,
} from "../Api/helpDesk";

import type {
  HelpDeskTicket,
  CreateTicketPayload,
  UpdateTicketPayload,
  CreateReplyPayload,
} from "../Models/helpDesk";

type HelpDeskProject = {
  id: number;
  name: string;
};

const unwrapResponse = (response: unknown): unknown => {
  if (response !== null && typeof response === "object" && "data" in response) {
    return (response as { data: unknown }).data;
  }

  return response;
};

const normalizeList = <T>(response: unknown, key: string): T[] => {
  const body = unwrapResponse(response);

  if (Array.isArray(body)) {
    return body as T[];
  }

  if (body !== null && typeof body === "object" && key in body) {
    const list = (body as Record<string, unknown>)[key];

    if (Array.isArray(list)) {
      return list as T[];
    }
  }

  return [];
};

export const useHelpDeskAccess = () => {
  return useQuery({
    queryKey: ["helpDeskAccess"],
    queryFn: getHelpDeskAccess,
  });
};

export const useHelpDeskProjects = () => {
  return useQuery({
    queryKey: ["helpDeskProjects"],
    queryFn: async (): Promise<HelpDeskProject[]> => {
      const response = await getHelpDeskProjects();
      return normalizeList<HelpDeskProject>(response, "projects");
    },
  });
};

export const useMyTickets = () => {
  return useQuery({
    queryKey: ["myTickets"],
    queryFn: async (): Promise<HelpDeskTicket[]> => {
      const response = await getMyTickets();
      return normalizeList<HelpDeskTicket>(response, "tickets");
    },
  });
};

export const useManagedTickets = (enabled = true) => {
  return useQuery({
    queryKey: ["managedTickets"],
    queryFn: async (): Promise<HelpDeskTicket[]> => {
      const response = await getManagedTickets();
      return normalizeList<HelpDeskTicket>(response, "tickets");
    },
    enabled,
  });
};

export const useSingleTicket = (id: number | undefined) => {
  return useQuery({
    queryKey: ["helpDeskTicket", id],
    queryFn: async () => {
      const response = await getSingleTicket(id!);
      return unwrapResponse(response) as HelpDeskTicket;
    },
    enabled: id !== undefined,
  });
};

export const useCreateTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateTicketPayload) => createTicket(payload),

    onSuccess: (ticket) => {
      queryClient.invalidateQueries({
        queryKey: ["myTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["managedTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["helpDeskTicket", ticket.id],
      });
    },
  });
};

export const useUpdateTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updatedTicket,
    }: {
      id: number;
      updatedTicket: UpdateTicketPayload;
    }) => updateTicket(id, updatedTicket),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["myTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["managedTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["helpDeskTicket", variables.id],
      });
    },
  });
};

export const useAddTicketReply = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reply }: { id: number; reply: CreateReplyPayload }) =>
      addTicketReply(id, reply),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["myTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["managedTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["helpDeskTicket", variables.id],
      });
    },
  });
};

export const useUploadTicketAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, files }: { id: number; files: File[] }) =>
      uploadTicketAttachments(id, files),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["helpDeskTicket", variables.id],
      });
    },
  });
};

export const useDeleteTicket = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteTicket(id),

    onSuccess: (_, id) => {
      queryClient.removeQueries({
        queryKey: ["helpDeskTicket", id],
      });

      queryClient.invalidateQueries({
        queryKey: ["myTickets"],
      });

      queryClient.invalidateQueries({
        queryKey: ["managedTickets"],
      });
    },
  });
};
